import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Briefcase, Wallet, Heart, Brain, Compass, ArrowLeft, LogOut, CheckCircle2, Lock } from "lucide-react";
import { api, track, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { getMe, loginWithGoogle, logoutUser } from "@/lib/auth";

const ICONS = { briefcase: Briefcase, wallet: Wallet, heart: Heart, brain: Brain, compass: Compass };

export default function SelectCategory() {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [user, setUser] = useState(null);
  const [done, setDone] = useState([]);
  const [paid, setPaid] = useState(null);
  const [priceLabel, setPriceLabel] = useState("Rp9.900");
  const [payBusy, setPayBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const me = await getMe();
        setUser(me);
      } catch {
        loginWithGoogle();
        return;
      }
      api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
      api.get("/user/today-status").then((r) => setDone(r.data.done || [])).catch(() => {});
      api.get("/purchases/status").then((r) => {
        setPaid(r.data.paid);
        if (r.data.price_label) setPriceLabel(r.data.price_label);
      }).catch(() => setPaid(false));
    })();
  }, []);

  useEffect(() => {
    if (paid !== false) return undefined;
    const iv = setInterval(() => {
      api.get("/purchases/status").then((r) => {
        if (r.data.paid) { setPaid(true); setWaiting(false); }
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(iv);
  }, [paid]);

  const buyAccess = async () => {
    setPayBusy(true);
    track("payment_started", null, { source: "gate" });
    try {
      const { data } = await api.post("/purchases");
      if (data.status === "paid") { setPaid(true); return; }
      const win = window.open(data.payment_url, "_blank");
      if (win) {
        try { win.opener = null; } catch (e) { /* noop */ }
        setWaiting(true);
      } else {
        toast.error("Popup diblokir browser. Izinkan popup, atau klik tombol lagi ya.");
      }
    } catch (e) {
      if (e.response?.status === 401) { loginWithGoogle(); return; }
      toast.error(formatErr(e.response?.data?.detail));
    } finally { setPayBusy(false); }
  };

  const checkPayment = async () => {
    try {
      const { data } = await api.get("/purchases/status");
      if (data.paid) { setPaid(true); setWaiting(false); }
      else toast("Pembayaran belum terkonfirmasi. Kalau baru aja bayar, tunggu beberapa detik ya.");
    } catch (e) { toast.error("Gagal cek status. Coba lagi ya."); }
  };

  const doLogout = async () => { await logoutUser(); navigate("/"); };
  const firstName = user?.name ? user.name.split(" ")[0] : "";

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 rounded-full border-2 border-amber-400/30 border-t-amber-300 animate-spin" /></div>;
  }

  return (
    <div className="relative min-h-screen w-full">
      <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 pt-6 pb-20">
        <div className="flex items-center justify-between mb-8">
          <button onClick={() => navigate("/")} data-testid="back-button"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-amber-200 transition-colors">
            <ArrowLeft size={16} /> Balik
          </button>
          <div className="flex items-center gap-3">
            {user.picture && <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full border border-amber-400/30" />}
            <button onClick={doLogout} data-testid="user-logout-button" className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-amber-200 transition-colors">
              <LogOut size={14} /> Keluar
            </button>
          </div>
        </div>

        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-sm text-amber-300/80 mb-2" data-testid="user-greeting">
          Halo, {firstName} 👋
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="font-display text-4xl sm:text-6xl font-semibold leading-tight">
          Apa yang lagi pengin lo <span className="gold-text">pahami?</span>
        </motion.h1>
        <p className="mt-4 text-gray-400 text-sm sm:text-base">
          Tiap kategori punya pertanyaannya sendiri. Lo bisa cek <span className="text-amber-200">1 kategori per hari</span> — biar jawaban lo tetap jujur.
        </p>

        {paid === false && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            className="mt-10 glass rounded-3xl p-8 sm:p-10 text-center" data-testid="payment-gate">
            <div className="w-12 h-12 mx-auto rounded-full bg-amber-400/15 border border-amber-400/40 flex items-center justify-center mb-5">
              <Lock size={20} className="text-amber-300" />
            </div>
            <h2 className="font-display text-2xl sm:text-3xl">Satu langkah lagi, {firstName}.</h2>
            <p className="mt-3 text-sm text-gray-400 max-w-sm mx-auto">
              Sekali bayar, <span className="text-amber-200">semua 5 kategori</span> kebuka selamanya —
              lengkap sama analisis mendalam tiap hasil.
            </p>
            <button onClick={buyAccess} disabled={payBusy || waiting} data-testid="gate-buy-button"
              className="mt-7 gold-btn rounded-full px-8 py-4 inline-flex items-center gap-3 disabled:opacity-60">
              {payBusy
                ? <span className="w-4 h-4 border-2 border-slate-900/40 border-t-slate-900 rounded-full animate-spin" />
                : <Lock size={17} />}
              {waiting ? "Menunggu pembayaran…" : `Buka Akses — ${priceLabel}`}
            </button>
            {waiting && (
              <div className="mt-4 flex flex-col items-center gap-2" data-testid="gate-waiting">
                <p className="text-xs text-amber-200/80 flex items-center justify-center gap-2">
                  <span className="w-3 h-3 border-2 border-amber-300/40 border-t-amber-300 rounded-full animate-spin" />
                  Checkout Lynk kebuka di tab baru. Selesaikan bayar di sana — halaman ini kebuka otomatis.
                </p>
                <button onClick={checkPayment} data-testid="gate-check-payment-button"
                  className="text-xs text-gray-400 underline underline-offset-2 hover:text-amber-200 transition-colors">
                  Cek Status Pembayaran
                </button>
              </div>
            )}
          </motion.div>
        )}

        {paid && (
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {cats.map((c, i) => {
            const Icon = ICONS[c.icon] || Compass;
            const isDone = done.includes(c.key);
            return (
              <motion.button
                key={c.key}
                data-testid={`category-${c.key}`}
                disabled={isDone}
                onClick={() => !isDone && navigate(`/mulai/${c.key}`)}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className={`group relative text-left card-surface rounded-2xl p-6 transition-all duration-300 overflow-hidden ${
                  isDone ? "opacity-50 cursor-not-allowed" : "hover:border-amber-400/50"
                }`}
              >
                <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 group-hover:opacity-25 transition-opacity" style={{ background: c.color }} />
                <div className="relative flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center border" style={{ background: `${c.color}22`, borderColor: `${c.color}44` }}>
                    <Icon size={22} style={{ color: c.color }} />
                  </div>
                  <div className="font-display text-2xl font-semibold text-white">{c.title}</div>
                  {isDone && (
                    <span className="ml-auto inline-flex items-center gap-1 text-xs text-emerald-300">
                      <CheckCircle2 size={14} /> Hari ini
                    </span>
                  )}
                </div>
                <p className="relative mt-4 text-sm text-gray-400 leading-relaxed">{c.subtitle}</p>
                {isDone && (
                  <p className="relative mt-3 text-xs text-amber-200/70 inline-flex items-center gap-1.5"><Lock size={12} /> Balik lagi besok ya</p>
                )}
              </motion.button>
            );
          })}
        </div>
        )}
      </div>
    </div>
  );
}
